// src/pages/OrderHistory.jsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  InputAdornment,
  Avatar,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
  CardContent,
  Tooltip,
  LinearProgress,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
  alpha,
  Skeleton,
  Menu,
  MenuItem,
  ListItemIcon,
  Badge,
  Collapse,
  Icon,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  Today as TodayIcon,
  DateRange as WeekIcon,
  CalendarMonth as MonthIcon,
  EventNote as AllIcon,
  Print as PrintIcon,
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  TableRestaurant as TableIcon,
  AccessTime as TimeIcon,
  AttachMoney as MoneyIcon,
  LocalOffer as DiscountIcon,
  Restaurant as FoodIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  PlayArrow as StartIcon,
  Timer as TimerIcon,
  CheckCircle as ReadyIcon,
  Warning as WarningIcon,
  MoreVert as MoreVertIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  CheckCircleOutline as PaidIcon,
  Pending as PendingIcon,
  DeliveryDining as DeliveryIcon,
  TakeoutDining as TakeawayIcon,
  Chair as ChairIcon,
  TrendingUp as TrendingIcon,
  Assessment as AssessmentIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval, parseISO } from 'date-fns';

const FILTER_PERIODS = [
  { value: 'today', label: 'Today', labelUrdu: 'آج', icon: <TodayIcon /> },
  { value: 'week', label: 'This Week', labelUrdu: 'اس ہفتے', icon: <WeekIcon /> },
  { value: 'month', label: 'This Month', labelUrdu: 'اس مہینے', icon: <MonthIcon /> },
  { value: 'all', label: 'All Time', labelUrdu: 'تمام', icon: <AllIcon /> },
];

const ORDER_TYPE_COLORS = {
  dine_in: { color: '#4caf50', bg: '#e8f5e9', icon: <ChairIcon /> },
  takeaway: { color: '#ff9800', bg: '#fff3e0', icon: <TakeawayIcon /> },
  delivery: { color: '#2196f3', bg: '#e3f2fd', icon: <DeliveryIcon /> },
  drive_thru: { color: '#9c27b0', bg: '#f3e5f5', icon: <FoodIcon /> },
};

const PAYMENT_STATUS_COLORS = {
  paid: { color: '#4caf50', bg: '#e8f5e9', label: 'Paid' },
  pending: { color: '#ff9800', bg: '#fff3e0', label: 'Pending' },
  partial: { color: '#2196f3', bg: '#e3f2fd', label: 'Partial' },
  failed: { color: '#f44336', bg: '#ffebee', label: 'Failed' },
};

const ORDER_STATUS_COLORS = {
  pending: { color: '#ff9800', bg: '#fff3e0', label: 'Pending' },
  preparing: { color: '#2196f3', bg: '#e3f2fd', label: 'Preparing' },
  ready: { color: '#4caf50', bg: '#e8f5e9', label: 'Ready' },
  served: { color: '#9c27b0', bg: '#f3e5f5', label: 'Served' },
  billed: { color: '#2e7d32', bg: '#e8f5e9', label: 'Billed' },
  cancelled: { color: '#f44336', bg: '#ffebee', label: 'Cancelled' },
};

const PREPARATION_TIME_MS = 30 * 60 * 1000;

const OrderHistory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('today');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderTypeFilter, setOrderTypeFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [activeTimers, setActiveTimers] = useState(() => {
    const saved = localStorage.getItem('orderTimers');
    return saved ? JSON.parse(saved) : {};
  });
  const [now, setNow] = useState(Date.now());
  const timerIntervalRef = useRef(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(isMobile ? 10 : 25);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [message, setMessage] = useState({ text: '', severity: 'success' });

  const [stats, setStats] = useState({
    totalOrders: 0,
    totalSales: 0,
    totalDiscount: 0,
    avgOrderValue: 0,
    dineInCount: 0,
    takeawayCount: 0,
    deliveryCount: 0,
  });

  // ========== STORAGE HELPERS ==========
  const getStorage = useCallback(async () => {
    try {
      const module = await import('../database/storage');
      const storage = module.default || module;
      await storage.init();
      return storage;
    } catch (e) {
      console.warn('[OrderHistory] Storage not available:', e);
      return null;
    }
  }, []);

  const getAPI = useCallback(() => {
    if (typeof window !== 'undefined' && window.electronAPI && window.electronAPI.isElectron === true) {
      return window.electronAPI;
    }
    return null;
  }, []);

  const showMessage = useCallback((text, severity = 'success') => {
    setMessage({ text, severity });
    setTimeout(() => setMessage({ text: '', severity: 'success' }), 4000);
  }, []);

  // ========== TIMER LOGIC ==========
  useEffect(() => {
    localStorage.setItem('orderTimers', JSON.stringify(activeTimers));
  }, [activeTimers]);

  useEffect(() => {
    timerIntervalRef.current = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => clearInterval(timerIntervalRef.current);
  }, []);

  const startTimer = useCallback((orderId) => {
    const startTime = Date.now();
    const endTime = startTime + PREPARATION_TIME_MS;
    setActiveTimers(prev => ({
      ...prev,
      [orderId]: { startTime, endTime, status: 'running' }
    }));
  }, []);

  const stopTimer = useCallback((orderId) => {
    setActiveTimers(prev => {
      const updated = { ...prev };
      delete updated[orderId];
      return updated;
    });
  }, []);

  const getTimerDisplay = useCallback((orderId) => {
    const timer = activeTimers[orderId];
    if (!timer) return null;

    const remaining = Math.max(0, timer.endTime - now);
    const total = PREPARATION_TIME_MS;
    const elapsed = total - remaining;
    const progress = Math.min(100, (elapsed / total) * 100);

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    let color = '#4caf50';
    let status = 'on_track';
    if (remaining <= 0) {
      color = '#f44336';
      status = 'overdue';
    } else if (remaining < 5 * 60 * 1000) {
      color = '#ff9800';
      status = 'urgent';
    }

    return {
      remaining,
      minutes,
      seconds: String(seconds).padStart(2, '0'),
      progress,
      color,
      status,
      isRunning: remaining > 0,
    };
  }, [activeTimers, now]);

  // ========== DELETE ORDER ==========
  const handleDeleteOrder = async () => {
    if (!orderToDelete) return;
    setDeleting(true);
    try {
      const api = getAPI();
      let success = false;

      if (api) {
        // Check if deleteOrder exists
        if (typeof api.deleteOrder === 'function') {
          await api.deleteOrder(orderToDelete.id);
          success = true;
        } else if (typeof api.dbQuery === 'function') {
          // Fallback using dbQuery
          await api.dbQuery('DELETE FROM orders WHERE id = ?', [orderToDelete.id]);
          await api.dbQuery('DELETE FROM order_items WHERE order_id = ?', [orderToDelete.id]);
          await api.dbQuery('DELETE FROM kot_log WHERE order_id = ?', [orderToDelete.id]);
          success = true;
        }
      } else {
        const storage = await getStorage();
        if (storage && typeof storage.deleteOrder === 'function') {
          await storage.deleteOrder(orderToDelete.id);
          success = true;
        } else if (storage && typeof storage.getOrders === 'function') {
          // Manual delete from localStorage fallback
          const savedOrders = localStorage.getItem('raath_orders');
          if (savedOrders) {
            const ordersData = JSON.parse(savedOrders);
            const filtered = ordersData.filter(o => o.id !== orderToDelete.id);
            localStorage.setItem('raath_orders', JSON.stringify(filtered));
            success = true;
          }
        }
      }

      if (success) {
        showMessage(`✅ Order #${orderToDelete.id} deleted successfully!`, 'success');
        setDeleteDialogOpen(false);
        setOrderToDelete(null);
        await loadOrders();
      } else {
        showMessage('❌ Delete operation not available', 'error');
      }
    } catch (err) {
      console.error('[OrderHistory] Delete error:', err);
      showMessage(`❌ Failed to delete order: ${err.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ========== DATA LOADING ==========
  const loadOrders = async () => {
    setLoading(true);
    try {
      const api = getAPI();
      let data = [];
      
      if (api) {
        console.log('[OrderHistory] Loading via Electron API');
        data = await api.getOrders({});
      } else {
        console.log('[OrderHistory] Loading via storage.js');
        const storage = await getStorage();
        if (storage && storage.getOrders) {
          data = await storage.getOrders({});
        } else {
          console.log('[OrderHistory] Fallback - localStorage');
          const savedOrders = localStorage.getItem('raath_orders');
          data = savedOrders ? JSON.parse(savedOrders) : [];
        }
      }

      console.log('[OrderHistory] Enriching orders with items...');
      const enrichedOrders = await Promise.all(data.map(async (order) => {
        try {
          if (api) {
            const full = await api.getOrderById(order.id);
            return full || order;
          } else {
            const storage = await getStorage();
            if (storage && storage.getOrderById) {
              const full = await storage.getOrderById(order.id);
              return full || order;
            }
            return order;
          }
        } catch (e) {
          console.warn(`[OrderHistory] Failed to enrich order ${order.id}:`, e);
          return order;
        }
      }));

      console.log('[OrderHistory] Loaded orders:', enrichedOrders.length);
      setOrders(enrichedOrders);
      applyFilters(enrichedOrders, period, searchQuery, orderTypeFilter, paymentFilter, statusFilter);
    } catch (err) {
      console.error('[OrderHistory] Load error:', err);
      showMessage('❌ Failed to load orders', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  const getDateRange = (p) => {
    const now = new Date();
    switch (p) {
      case 'today':
        return { from: startOfDay(now), to: endOfDay(now) };
      case 'week':
        return { from: startOfWeek(now, { weekStartsOn: 1 }), to: endOfWeek(now, { weekStartsOn: 1 }) };
      case 'month':
        return { from: startOfMonth(now), to: endOfMonth(now) };
      case 'custom':
        return {
          from: customFrom ? parseISO(customFrom) : startOfDay(now),
          to: customTo ? parseISO(customTo) : endOfDay(now),
        };
      default:
        return { from: null, to: null };
    }
  };

  const applyFilters = (data, p, search, typeFilter, payFilter, statFilter) => {
    let filtered = [...data];

    const { from, to } = getDateRange(p);
    if (from && to) {
      filtered = filtered.filter((order) => {
        const orderDate = parseISO(order.created_at);
        return isWithinInterval(orderDate, { start: from, end: to });
      });
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      filtered = filtered.filter((order) =>
        (order.customer_name?.toLowerCase() || '').includes(q) ||
        (order.customer_phone?.toLowerCase() || '').includes(q) ||
        String(order.id).includes(q) ||
        (order.table_number?.toLowerCase() || '').includes(q) ||
        (order.items?.some(i => i.item_name?.toLowerCase().includes(q)) || false)
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter((order) => order.order_type === typeFilter);
    }

    if (payFilter !== 'all') {
      filtered = filtered.filter((order) => order.payment_status === payFilter);
    }

    if (statFilter !== 'all') {
      filtered = filtered.filter((order) => order.status === statFilter);
    }

    setFilteredOrders(filtered);
    calculateStats(filtered);
    setPage(0);
  };

  const calculateStats = (data) => {
    const totalSales = data.reduce((sum, o) => sum + (o.final_amount || o.total_amount || 0), 0);
    const totalDiscount = data.reduce((sum, o) => sum + (o.discount_amount || 0), 0);
    setStats({
      totalOrders: data.length,
      totalSales,
      totalDiscount,
      avgOrderValue: data.length > 0 ? totalSales / data.length : 0,
      dineInCount: data.filter((o) => o.order_type === 'dine_in').length,
      takeawayCount: data.filter((o) => o.order_type === 'takeaway').length,
      deliveryCount: data.filter((o) => o.order_type === 'delivery').length,
    });
  };

  useEffect(() => {
    if (orders.length > 0) {
      applyFilters(orders, period, searchQuery, orderTypeFilter, paymentFilter, statusFilter);
    }
  }, [period, searchQuery, orderTypeFilter, paymentFilter, statusFilter, customFrom, customTo]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openDetail = async (order) => {
    try {
      const api = getAPI();
      let fullOrder;
      
      if (api) {
        fullOrder = await api.getOrderById(order.id);
      } else {
        const storage = await getStorage();
        if (storage && storage.getOrderById) {
          fullOrder = await storage.getOrderById(order.id);
        } else {
          fullOrder = order;
        }
      }
      setSelectedOrder(fullOrder || order);
      setDetailOpen(true);
    } catch (err) {
      console.error('[OrderHistory] Detail load error:', err);
      setSelectedOrder(order);
      setDetailOpen(true);
    }
  };

  const openDeleteDialog = (order, e) => {
    e?.stopPropagation();
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const formatDate = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'dd MMM yyyy');
    } catch {
      return timestamp || 'N/A';
    }
  };

  const formatTime = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'hh:mm a');
    } catch {
      return timestamp || 'N/A';
    }
  };

  const formatDateTime = (timestamp) => {
    try {
      return format(parseISO(timestamp), 'dd MMM yyyy, hh:mm a');
    } catch {
      return timestamp || 'N/A';
    }
  };

  const formatPrice = (amount) => `PKR ${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

  const getOrderTypeDisplay = (type) => {
    const displays = {
      dine_in: { label: 'Dine In', labelUrdu: 'بیٹھ کر', icon: <ChairIcon fontSize="small" /> },
      takeaway: { label: 'Takeaway', labelUrdu: 'پارسل', icon: <TakeawayIcon fontSize="small" /> },
      delivery: { label: 'Delivery', labelUrdu: 'ڈلیوری', icon: <DeliveryIcon fontSize="small" /> },
      drive_thru: { label: 'Drive Thru', labelUrdu: 'ڈرائیو تھرو', icon: <FoodIcon fontSize="small" /> },
    };
    return displays[type] || displays.dine_in;
  };

  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const handleFilterOpen = (event) => setFilterAnchorEl(event.currentTarget);
  const handleFilterClose = () => setFilterAnchorEl(null);

  const clearAllFilters = () => {
    setSearchQuery('');
    setOrderTypeFilter('all');
    setPaymentFilter('all');
    setStatusFilter('all');
    setPeriod('today');
    setCustomFrom('');
    setCustomTo('');
    handleFilterClose();
  };

  const paginatedOrders = filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // ========== MEMOIZED STATS CARDS ==========
  const statCards = useMemo(() => [
    { 
      title: 'Total Orders', 
      titleUrdu: 'کل آرڈرز', 
      value: stats.totalOrders, 
      icon: <ReceiptIcon />, 
      color: '#1976d2', 
      bg: alpha('#1976d2', 0.1),
    },
    { 
      title: 'Total Sales', 
      titleUrdu: 'کل فروخت', 
      value: formatPrice(stats.totalSales), 
      icon: <MoneyIcon />, 
      color: '#2e7d32', 
      bg: alpha('#2e7d32', 0.1),
    },
    { 
      title: 'Avg Order', 
      titleUrdu: 'اوسط بل', 
      value: formatPrice(stats.avgOrderValue), 
      icon: <TrendingIcon />, 
      color: '#ed6c02', 
      bg: alpha('#ed6c02', 0.1),
    },
    { 
      title: 'Discounts', 
      titleUrdu: 'رعایت', 
      value: formatPrice(stats.totalDiscount), 
      icon: <DiscountIcon />, 
      color: '#d32f2f', 
      bg: alpha('#d32f2f', 0.1),
    },
  ], [stats, formatPrice]);

  const renderStatsCards = () => (
    <Grid container spacing={isMobile ? 1.5 : 2} sx={{ mb: 3 }}>
      {statCards.map((stat, idx) => (
        <Grid item xs={6} sm={6} md={3} key={idx}>
          <Fade in timeout={300 + idx * 100}>
            <Card sx={{ 
              borderRadius: 3, 
              border: `1px solid ${stat.color}20`,
              bgcolor: 'background.paper',
              transition: 'all 0.3s',
              '&:hover': { 
                transform: 'translateY(-4px)',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
              },
            }}>
              <CardContent sx={{ p: isMobile ? 1.5 : 2, '&:last-child': { pb: isMobile ? 1.5 : 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ bgcolor: stat.bg, color: stat.color, width: isMobile ? 36 : 40, height: isMobile ? 36 : 40 }}>
                    {stat.icon}
                  </Avatar>
                  <Box>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      sx={{ fontWeight: 700, color: stat.color, fontSize: isMobile ? '1rem' : '1.2rem' }}
                    >
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      {stat.title} | {stat.titleUrdu}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      ))}
    </Grid>
  );

  const renderOrderTypeChips = () => (
    <Paper sx={{ 
      p: isMobile ? 1.5 : 2, 
      mb: 2, 
      borderRadius: 3,
      bgcolor: 'background.paper',
    }}>
      <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 2} alignItems={isMobile ? "stretch" : "center"}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {[
            { type: 'dine_in', count: stats.dineInCount, label: 'Dine In', labelUrdu: 'بیٹھ کر', icon: <ChairIcon /> },
            { type: 'takeaway', count: stats.takeawayCount, label: 'Takeaway', labelUrdu: 'پارسل', icon: <TakeawayIcon /> },
            { type: 'delivery', count: stats.deliveryCount, label: 'Delivery', labelUrdu: 'ڈلیوری', icon: <DeliveryIcon /> },
          ].map((item) => (
            <Chip
              key={item.type}
              icon={item.icon}
              label={
                <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {item.label}
                  <Badge badgeContent={item.count} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 18, minWidth: 18 } }} />
                </Box>
              }
              onClick={() => setOrderTypeFilter(orderTypeFilter === item.type ? 'all' : item.type)}
              sx={{
                bgcolor: orderTypeFilter === item.type ? `${ORDER_TYPE_COLORS[item.type]?.color}20` : 'transparent',
                color: orderTypeFilter === item.type ? ORDER_TYPE_COLORS[item.type]?.color : 'text.secondary',
                border: `1px solid ${orderTypeFilter === item.type ? ORDER_TYPE_COLORS[item.type]?.color : 'divider'}`,
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': { 
                  bgcolor: `${ORDER_TYPE_COLORS[item.type]?.color}10`,
                  transform: 'scale(1.02)',
                },
              }}
            />
          ))}
        </Box>
        
        <Box sx={{ flex: 1 }} />
        
        <TextField
          size="small"
          placeholder="Search orders..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: isMobile ? '100%' : 250,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        />
      </Stack>
    </Paper>
  );

  const renderFilters = () => (
    <Paper sx={{ 
      p: isMobile ? 1.5 : 2, 
      mb: 2, 
      borderRadius: 3,
      bgcolor: 'background.paper',
    }}>
      <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1.5 : 2} alignItems={isMobile ? "stretch" : "center"}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          <FilterIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          Period:
        </Typography>
        <ToggleButtonGroup
          value={period}
          exclusive
          onChange={(e, val) => val && setPeriod(val)}
          size="small"
          sx={{
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: isMobile ? 0.5 : 0,
            '& .MuiToggleButton-root': {
              borderRadius: '20px !important',
              px: isMobile ? 1 : 2,
              py: 0.5,
              textTransform: 'none',
              fontWeight: 500,
              fontSize: isMobile ? '0.7rem' : '0.8rem',
              mx: 0.5,
            },
            '& .Mui-selected': {
              bgcolor: '#1a1a2e !important',
              color: '#fff !important',
            },
          }}
        >
          {FILTER_PERIODS.map((p) => (
            <ToggleButton key={p.value} value={p.value}>
              {p.icon}
              <Box component="span" sx={{ ml: 0.5, display: isMobile && p.value !== 'today' ? 'none' : 'inline' }}>
                {isMobile ? p.label.charAt(0) : p.label}
              </Box>
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {period === 'custom' && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              type="date"
              size="small"
              label="From"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: isMobile ? '100%' : 150 }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: isMobile ? '100%' : 150 }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1 }} />

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Tooltip title="Filter by Payment Status">
            <Chip
              size="small"
              label={paymentFilter === 'all' ? 'All Payment' : paymentFilter}
              onClick={handleFilterOpen}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Filter by Order Status">
            <Chip
              size="small"
              label={statusFilter === 'all' ? 'All Status' : statusFilter}
              onClick={(e) => {
                const statuses = ['all', 'pending', 'preparing', 'ready', 'served', 'billed'];
                const currentIndex = statuses.indexOf(statusFilter);
                const nextIndex = (currentIndex + 1) % statuses.length;
                setStatusFilter(statuses[nextIndex]);
              }}
              sx={{ cursor: 'pointer' }}
            />
          </Tooltip>
          {(paymentFilter !== 'all' || statusFilter !== 'all' || searchQuery || period !== 'today') && (
            <IconButton size="small" onClick={clearAllFilters} sx={{ color: '#f44336' }}>
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Stack>

      <Menu
        anchorEl={filterAnchorEl}
        open={Boolean(filterAnchorEl)}
        onClose={handleFilterClose}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 200 } }}
      >
        <MenuItem disabled>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Payment Status</Typography>
        </MenuItem>
        <MenuItem onClick={() => { setPaymentFilter('all'); handleFilterClose(); }}>
          <ListItemIcon><ClearIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="All" />
        </MenuItem>
        {Object.entries(PAYMENT_STATUS_COLORS).map(([key, val]) => (
          <MenuItem key={key} onClick={() => { setPaymentFilter(key); handleFilterClose(); }}>
            <ListItemIcon><Icon sx={{ color: val.color }}>●</Icon></ListItemIcon>
            <ListItemText primary={val.label} />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Order Status</Typography>
        </MenuItem>
        <MenuItem onClick={() => { setStatusFilter('all'); handleFilterClose(); }}>
          <ListItemIcon><ClearIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="All" />
        </MenuItem>
        {Object.entries(ORDER_STATUS_COLORS).map(([key, val]) => (
          <MenuItem key={key} onClick={() => { setStatusFilter(key); handleFilterClose(); }}>
            <ListItemIcon><Icon sx={{ color: val.color }}>●</Icon></ListItemIcon>
            <ListItemText primary={val.label} />
          </MenuItem>
        ))}
      </Menu>
    </Paper>
  );

  const renderMobileOrderCard = (order) => {
    const typeInfo = getOrderTypeDisplay(order.order_type);
    const typeColors = ORDER_TYPE_COLORS[order.order_type] || ORDER_TYPE_COLORS.dine_in;
    const timer = getTimerDisplay(order.id);
    const paymentInfo = PAYMENT_STATUS_COLORS[order.payment_status] || PAYMENT_STATUS_COLORS.pending;

    return (
      <Card
        key={order.id}
        sx={{
          mb: 1.5,
          borderRadius: 3,
          border: `1px solid ${alpha('#1a1a2e', 0.08)}`,
          cursor: 'pointer',
          transition: 'all 0.2s',
          '&:hover': { 
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            transform: 'translateY(-2px)',
          },
          ...(timer && timer.status === 'urgent' && { borderColor: '#ff9800', bgcolor: '#fff8e1' }),
          ...(timer && timer.status === 'overdue' && { borderColor: '#f44336', bgcolor: '#ffebee' }),
        }}
        onClick={() => openDetail(order)}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                #{order.id}
                {order.kot_number && (
                  <Typography component="span" variant="caption" sx={{ color: 'text.disabled', ml: 1 }}>
                    KOT: {order.kot_number}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon fontSize="inherit" /> {formatDateTime(order.created_at)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip
                size="small"
                label={paymentInfo.label}
                sx={{ 
                  bgcolor: paymentInfo.bg, 
                  color: paymentInfo.color, 
                  fontWeight: 600, 
                  fontSize: '0.6rem',
                  height: 20,
                }}
              />
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleMenuOpen(e) }}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              {order.customer_name ? (
                <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonIcon fontSize="small" color="action" /> {order.customer_name}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>Walk-in Customer</Typography>
              )}
              {order.customer_phone && (
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PhoneIcon fontSize="inherit" /> {order.customer_phone}
                </Typography>
              )}
            </Box>
            <Chip
              size="small"
              icon={typeInfo.icon}
              label={typeInfo.label}
              sx={{
                bgcolor: typeColors.bg,
                color: typeColors.color,
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 24,
              }}
            />
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {order.items?.length || 0} items
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
              {formatPrice(order.final_amount || order.total_amount)}
            </Typography>
          </Box>

          {timer && (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: timer.color, fontWeight: 600 }}>
                  <TimerIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                  {timer.remaining > 0 ? `${timer.minutes}:${timer.seconds}` : 'OVERDUE'}
                </Typography>
                <Button
                  size="small"
                  variant="contained"
                  color="success"
                  onClick={(e) => { e.stopPropagation(); stopTimer(order.id); }}
                  sx={{ fontSize: '0.6rem', py: 0, px: 1, minWidth: 0, height: 20 }}
                >
                  Ready ✓
                </Button>
              </Box>
              <LinearProgress
                variant="determinate"
                value={timer.progress}
                sx={{
                  height: 3,
                  borderRadius: 2,
                  bgcolor: 'rgba(0,0,0,0.05)',
                  '& .MuiLinearProgress-bar': { bgcolor: timer.color, borderRadius: 2 },
                }}
              />
            </Box>
          )}

          {!timer && order.status !== 'billed' && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<StartIcon />}
              onClick={(e) => { e.stopPropagation(); startTimer(order.id); }}
              sx={{ mt: 1, fontSize: '0.6rem', py: 0.5 }}
            >
              Start Timer
            </Button>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDesktopTable = () => (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: '#1a1a2e' }}>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>#</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Date & Time</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Customer</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Type</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Items</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="right">Amount</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Payment</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Timer</TableCell>
            <TableCell sx={{ color: '#fff', fontWeight: 700 }} align="center">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {paginatedOrders.map((order) => {
            const typeInfo = getOrderTypeDisplay(order.order_type);
            const typeColors = ORDER_TYPE_COLORS[order.order_type] || ORDER_TYPE_COLORS.dine_in;
            const timer = getTimerDisplay(order.id);
            const paymentInfo = PAYMENT_STATUS_COLORS[order.payment_status] || PAYMENT_STATUS_COLORS.pending;
            const statusInfo = ORDER_STATUS_COLORS[order.status] || ORDER_STATUS_COLORS.pending;

            return (
              <TableRow
                key={order.id}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha('#1a1a2e', 0.03) },
                  ...(timer && timer.status === 'urgent' && { bgcolor: '#fff8e1 !important' }),
                  ...(timer && timer.status === 'overdue' && { bgcolor: '#ffebee !important' }),
                }}
                onClick={() => openDetail(order)}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                    #{order.id}
                  </Typography>
                  {order.kot_number && (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      KOT: {order.kot_number}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatDate(order.created_at)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TimeIcon fontSize="inherit" /> {formatTime(order.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  {order.customer_name ? (
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonIcon fontSize="small" color="action" /> {order.customer_name}
                      </Typography>
                      {order.customer_phone && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <PhoneIcon fontSize="inherit" /> {order.customer_phone}
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                      Walk-in
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    icon={typeInfo.icon}
                    label={typeInfo.label}
                    sx={{
                      bgcolor: typeColors.bg,
                      color: typeColors.color,
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      height: 26,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {order.items?.length || 0} items
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32' }}>
                    {formatPrice(order.final_amount || order.total_amount)}
                  </Typography>
                  {order.discount_amount > 0 && (
                    <Typography variant="caption" sx={{ color: '#f44336', textDecoration: 'line-through' }}>
                      {formatPrice(order.total_amount)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={paymentInfo.label}
                    sx={{
                      bgcolor: paymentInfo.bg,
                      color: paymentInfo.color,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 22,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={statusInfo.label}
                    sx={{
                      bgcolor: statusInfo.bg,
                      color: statusInfo.color,
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: 22,
                    }}
                  />
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                  {timer ? (
                    <Box sx={{ minWidth: 100 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mb: 0.5 }}>
                        {timer.status === 'overdue' ? (
                          <WarningIcon sx={{ color: '#f44336', fontSize: 16 }} />
                        ) : (
                          <TimerIcon sx={{ color: timer.color, fontSize: 16 }} />
                        )}
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            color: timer.color,
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                          }}
                        >
                          {timer.remaining > 0 ? `${timer.minutes}:${timer.seconds}` : 'OVERDUE'}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={timer.progress}
                        sx={{
                          height: 3,
                          borderRadius: 2,
                          bgcolor: 'rgba(0,0,0,0.05)',
                          '& .MuiLinearProgress-bar': { bgcolor: timer.color, borderRadius: 2 },
                        }}
                      />
                      <Button
                        size="small"
                        variant="text"
                        color="success"
                        sx={{ fontSize: '0.6rem', py: 0, px: 0.5, minWidth: 0 }}
                        onClick={() => stopTimer(order.id)}
                      >
                        ✓ Ready
                      </Button>
                    </Box>
                  ) : order.status !== 'billed' ? (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<StartIcon />}
                      onClick={() => startTimer(order.id)}
                      sx={{
                        bgcolor: '#1a1a2e',
                        fontSize: '0.6rem',
                        py: 0.2,
                        px: 1,
                        minWidth: 0,
                        '&:hover': { bgcolor: '#333' },
                      }}
                    >
                      Start
                    </Button>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      —
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); openDetail(order); }}>
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Print Bill">
                    <IconButton size="small" onClick={(e) => e.stopPropagation()}>
                      <PrintIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Order">
                    <IconButton 
                      size="small" 
                      onClick={(e) => { e.stopPropagation(); openDeleteDialog(order, e); }}
                      sx={{ color: '#f44336' }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box sx={{ 
      p: isMobile ? 1.5 : isTablet ? 2 : 3,
      maxWidth: 1600,
      mx: 'auto',
      width: '100%',
    }}>
      {message.text && (
        <Alert severity={message.severity} sx={{ mb: 2, borderRadius: 2 }}>
          {message.text}
        </Alert>
      )}

      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        gap: 2 
      }}>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
            Order History
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            آرڈر ہسٹری | Past Orders & Invoices
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ 
              borderRadius: 2, 
              fontWeight: 600,
              flex: isMobile ? 1 : 'none',
            }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadOrders}
            disabled={loading}
            sx={{ 
              bgcolor: '#ff6b35', 
              '&:hover': { bgcolor: '#e55a2b' }, 
              fontWeight: 600, 
              borderRadius: 2,
              flex: isMobile ? 1 : 'none',
            }}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {renderStatsCards()}
      {renderOrderTypeChips()}
      {renderFilters()}

      {loading ? (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2 }} />
          </Stack>
        </Paper>
      ) : (
        <Paper sx={{ 
          borderRadius: 3, 
          overflow: 'hidden', 
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          bgcolor: 'background.paper',
        }}>
          {isMobile ? (
            <Box sx={{ p: 1.5 }}>
              {paginatedOrders.length > 0 ? (
                paginatedOrders.map((order) => renderMobileOrderCard(order))
              ) : (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                  <ReceiptIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                    No orders found
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                    اس مدت میں کوئی آرڈر نہیں
                  </Typography>
                  <Button 
                    variant="outlined" 
                    startIcon={<RefreshIcon />} 
                    onClick={loadOrders}
                    sx={{ mt: 2 }}
                  >
                    Refresh
                  </Button>
                </Box>
              )}
            </Box>
          ) : (
            renderDesktopTable()
          )}

          {filteredOrders.length > 0 && (
            <TablePagination
              component="div"
              count={filteredOrders.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={isMobile ? [5, 10, 25] : [10, 25, 50, 100]}
              labelRowsPerPage={isMobile ? "Rows:" : "Rows per page:"}
              sx={{
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                },
              }}
            />
          )}
        </Paper>
      )}

      {/* DELETE CONFIRM DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f' }}>
          <DeleteIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Delete Order?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete Order #{orderToDelete?.id}? This action cannot be undone.
          </Typography>
          {orderToDelete && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Customer: {orderToDelete.customer_name || 'Walk-in'}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Amount: {formatPrice(orderToDelete.final_amount || orderToDelete.total_amount)}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                Date: {formatDateTime(orderToDelete.created_at)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            variant="outlined"
            disabled={deleting}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteOrder} 
            variant="contained" 
            color="error"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            sx={{ borderRadius: 2 }}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ORDER DETAIL DIALOG */}
      <Dialog 
        open={detailOpen} 
        onClose={() => setDetailOpen(false)} 
        maxWidth="sm" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2,
          } 
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 700, 
          color: '#1a1a2e', 
          bgcolor: '#f8f9fa',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: isMobile ? 1.5 : 2,
        }}>
          Order #{selectedOrder?.id} Details
          <IconButton onClick={() => setDetailOpen(false)} sx={{ ml: 1 }}>
            <BackIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          {selectedOrder && (
            <Box>
              <Paper sx={{ 
                p: isMobile ? 1.5 : 2, 
                mb: 2, 
                borderRadius: 2, 
                bgcolor: '#f8f9fa' 
              }}>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Order Date</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      {formatDateTime(selectedOrder.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Order Type</Typography>
                    <Box>
                      <Chip
                        size="small"
                        icon={getOrderTypeDisplay(selectedOrder.order_type).icon}
                        label={getOrderTypeDisplay(selectedOrder.order_type).label}
                        sx={{
                          bgcolor: ORDER_TYPE_COLORS[selectedOrder.order_type]?.bg,
                          color: ORDER_TYPE_COLORS[selectedOrder.order_type]?.color,
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Customer</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      {selectedOrder.customer_name || 'Walk-in'}
                    </Typography>
                    {selectedOrder.customer_phone && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {selectedOrder.customer_phone}
                      </Typography>
                    )}
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Table</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      {selectedOrder.table_number || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Payment</Typography>
                    <Chip
                      size="small"
                      label={selectedOrder.payment_status || 'pending'}
                      sx={{
                        bgcolor: `${PAYMENT_STATUS_COLORS[selectedOrder.payment_status]?.color || '#ff9800'}15`,
                        color: PAYMENT_STATUS_COLORS[selectedOrder.payment_status]?.color || '#ff9800',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>Status</Typography>
                    <Chip
                      size="small"
                      label={selectedOrder.status || 'pending'}
                      sx={{
                        bgcolor: `${ORDER_STATUS_COLORS[selectedOrder.status]?.color || '#ff9800'}15`,
                        color: ORDER_STATUS_COLORS[selectedOrder.status]?.color || '#ff9800',
                        fontWeight: 600,
                        fontSize: '0.65rem',
                      }}
                    />
                  </Grid>
                </Grid>
              </Paper>

              {(() => {
                const timer = getTimerDisplay(selectedOrder.id);
                if (!timer) return null;
                return (
                  <Paper sx={{ 
                    p: isMobile ? 1.5 : 2, 
                    mb: 2, 
                    borderRadius: 2, 
                    bgcolor: timer.status === 'overdue' ? '#ffebee' : timer.status === 'urgent' ? '#fff8e1' : '#e8f5e9' 
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: timer.color }}>
                        <TimerIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                        Kitchen Timer
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 700, color: timer.color, fontFamily: 'monospace' }}>
                        {timer.remaining > 0 ? `${timer.minutes}:${timer.seconds}` : 'OVERDUE'}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={timer.progress}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: 'rgba(0,0,0,0.05)',
                        '& .MuiLinearProgress-bar': { bgcolor: timer.color, borderRadius: 4 },
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Target: 30 minutes
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => stopTimer(selectedOrder.id)}
                        sx={{ fontSize: '0.7rem' }}
                      >
                        Mark Ready ✓
                      </Button>
                    </Box>
                  </Paper>
                );
              })()}

              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#1a1a2e' }}>
                Items Ordered / آرڈر کی اشیاء
              </Typography>
              <List dense sx={{ bgcolor: '#f8f9fa', borderRadius: 2, mb: 2 }}>
                {selectedOrder.items?.map((item, idx) => (
                  <React.Fragment key={idx}>
                    <ListItem sx={{ py: isMobile ? 1 : 1.5 }}>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                              {idx + 1}. {item.item_name} x{item.quantity}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                              {formatPrice((item.unit_price + (item.modifiers_price || 0)) * item.quantity)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            {item.modifiers && (() => {
                              try {
                                const mods = typeof item.modifiers === 'string' ? JSON.parse(item.modifiers) : item.modifiers;
                                return Array.isArray(mods) && mods.length > 0 && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    + {mods.join(', ')}
                                  </Typography>
                                );
                              } catch { return null; }
                            })()}
                            {item.notes && (
                              <Typography variant="caption" sx={{ color: '#ff9800', display: 'block' }}>
                                📝 {item.notes}
                              </Typography>
                            )}
                            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                              Unit: {formatPrice(item.unit_price)} each
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {idx < selectedOrder.items.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>

              <Paper sx={{ 
                p: isMobile ? 1.5 : 2, 
                borderRadius: 2, 
                bgcolor: '#1a1a2e', 
                color: '#fff' 
              }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, color: '#ff6b35' }}>
                  Bill Summary / بل خلاصہ
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    Subtotal
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    {formatPrice(selectedOrder.total_amount)}
                  </Typography>
                </Box>
                {selectedOrder.service_charge > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      Service Charge
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      {formatPrice(selectedOrder.service_charge)}
                    </Typography>
                  </Box>
                )}
                {selectedOrder.tax_amount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      Tax
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      {formatPrice(selectedOrder.tax_amount)}
                    </Typography>
                  </Box>
                )}
                {selectedOrder.discount_amount > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ color: '#f44336', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      Discount
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#f44336', fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                      -{formatPrice(selectedOrder.discount_amount)}
                    </Typography>
                  </Box>
                )}
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 1.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700 }}>
                    Total
                  </Typography>
                  <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 700, color: '#4caf50' }}>
                    {formatPrice(selectedOrder.final_amount)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                    Payment: {selectedOrder.payment_method?.toUpperCase() || 'N/A'}
                  </Typography>
                  <Chip
                    size="small"
                    label={selectedOrder.payment_status || 'pending'}
                    sx={{
                      bgcolor: `${PAYMENT_STATUS_COLORS[selectedOrder.payment_status]?.color || '#ff9800'}30`,
                      color: PAYMENT_STATUS_COLORS[selectedOrder.payment_status]?.color || '#ff9800',
                      fontWeight: 600,
                      fontSize: '0.6rem',
                      height: 20,
                    }}
                  />
                </Box>
              </Paper>

              {selectedOrder.notes && (
                <Box sx={{ 
                  mt: 2, 
                  p: 1.5, 
                  bgcolor: '#fff3e0', 
                  borderRadius: 2, 
                  border: '1px dashed #ff9800' 
                }}>
                  <Typography variant="caption" sx={{ color: '#ed6c02', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    📝 {selectedOrder.notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          px: isMobile ? 2 : 3, 
          pb: isMobile ? 2 : 2,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 1 : 0,
        }}>
          <Button onClick={() => setDetailOpen(false)} variant="outlined" fullWidth={isMobile}>
            Close
          </Button>
          <Button 
            variant="contained" 
            startIcon={<PrintIcon />} 
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' } }}
            fullWidth={isMobile}
          >
            Print Bill
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderHistory;